import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/vansha/PublicNav";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Check, Crown } from "@phosphor-icons/react";

const STYLES = {
  free: { card: "bg-white border border-ink-10", btn: "btn-secondary", price: "text-[#1B1533]" },
  plus: { card: "bg-white border-2 border-ink-20", btn: "btn-secondary", price: "text-[#1B1533]" },
  care: { card: "bg-white border-2 border-terracotta shadow-xl", btn: "btn-primary", price: "text-terracotta", badge: "Most loved" },
  signature: { card: "bg-forest text-white border-2 border-forest", btn: "bg-saffron text-forest hover:bg-[#b87a39] rounded-full px-6 py-3 font-medium transition", price: "text-saffron" },
};

export default function Pricing({ inApp = false }) {
  const { user } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/membership/tiers").then((r) => setTiers(r.data));
  }, []);

  const select = async (id) => {
    if (!user) return;
    setBusy(id);
    try {
      await api.post(`/membership/select/${id}`);
      toast.success("Membership updated. Welcome to " + id + ".");
    } catch (e) {
      toast.error("Could not update membership.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className={inApp ? "" : "min-h-screen"} data-testid="pricing-page">
      {!inApp && <PublicNav />}
      <section className={inApp ? "" : "vansha-section"}>
        <div className={inApp ? "" : "vansha-container"}>
          <div className="max-w-2xl mb-12">
            <p className="overline mb-4 text-saffron">Memberships</p>
            <h1 className="font-serif font-semibold text-4xl sm:text-5xl text-[#1B1533] leading-tight">Quiet luxury for your family's<br /><em>most sacred chapter.</em></h1>
            <p className="mt-6 text-lg text-[#3D2E5C]">Begin free. Upgrade whenever the moment is right. Cancel anytime.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((t) => {
              const s = STYLES[t.id] || STYLES.plus;
              const isLegacy = t.id === "signature";
              return (
                <div key={t.id} className={`${s.card} rounded-3xl p-8 relative flex flex-col`} data-testid={`pricing-card-${t.id}`}>
                  {(t.popular || s.badge) && (
                    <span className="absolute -top-3 right-6 bg-terracotta text-white text-xs uppercase tracking-widest px-3 py-1 rounded-full" data-testid={`badge-${t.id}`}>
                      {s.badge || "Popular"}
                    </span>
                  )}
                  {isLegacy && <Crown weight="thin" size={28} className="text-saffron mb-3" />}
                  <h3 className="font-serif text-2xl mb-1">{t.name}</h3>
                  <p className={`text-sm mb-6 ${isLegacy ? "opacity-80" : "text-[#7A6E96]"}`}>{t.tagline}</p>
                  <div className="mb-6">
                    <span className={`font-serif font-semibold text-5xl ${s.price}`}>
                      {t.price === 0 ? "Free" : `₹${t.price.toLocaleString("en-IN")}`}
                    </span>
                    {t.price > 0 && <span className={`text-sm ml-2 ${isLegacy ? "opacity-70" : "text-[#7A6E96]"}`}>/month</span>}
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {t.features.map((f, i) => (
                      <li key={i} className="flex gap-3 text-sm">
                        <Check weight="thin" size={18} className={isLegacy ? "text-saffron flex-shrink-0 mt-0.5" : "text-terracotta flex-shrink-0 mt-0.5"} />
                        <span className={isLegacy ? "opacity-90" : "text-[#3D2E5C]"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {user ? (
                    <button onClick={() => select(t.id)} disabled={busy === t.id || user.membership_tier === t.id} className={s.btn} data-testid={`select-${t.id}`}>
                      {user.membership_tier === t.id ? "Your plan" : busy === t.id ? "…" : t.price === 0 ? "Choose free" : "Choose plan"}
                    </button>
                  ) : (
                    <Link to="/register" className={`${s.btn} text-center`} data-testid={`select-${t.id}`}>
                      Begin
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-10 text-sm text-[#7A6E96] text-center">All prices in INR. Payments coming soon — current plans are complimentary during early access.</p>
        </div>
      </section>
    </div>
  );
}
